import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Participation,
  SessionStatus,
  useMe,
  useParticipations,
  usePublicSession,
} from '@app/api';
import {
  CenteredLoader,
  NotFound,
  ProgressLine,
  useProfileCompletion,
} from '@app/core';
import { TandemzLogoHeader } from '../components/TandemzLogoHeader';
import { CardHomeBase, StyleCardHome } from './components/CardHomeBase';
import { useTranslation } from 'react-i18next';
import { BookedParticipationCard } from './components/BookedParticipationCard';
import { AcceptedParticipationCard } from './components/AcceptedParticipationCard';
import classnames from 'classnames';
import { ApplyToStudiesCard } from './components/ApplyToStudiesCard';
import { useParticipationsByColumns } from '../components/useParticipationsByStatus';
import { VerifiedProfileIconsCard } from './components/VerifiedProfileIconsCard';

const useStyles = createUseStyles((theme: Theme) => ({
  page: {
    backgroundColor: theme.colors.inkPrimary,
    minHeight: '100%',
    width: '100%',
    maxWidth: theme.maxContentWidth.hugeBlock,
    margin: '0 auto',
  },
  page2Column: {
    maxWidth: (theme.maxContentWidth.hugeBlock / 3) * 2,
  },
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
    [theme.media.homeScreen]: {
      padding: [0, theme.marginBase * 2],
      paddingBottom: theme.marginBase * 2,
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
  },
  header: {
    width: '100%',
    padding: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 4,
    [theme.media.homeScreen]: {
      paddingTop: theme.marginBase * 7,
    },
  },
  title: {
    ...theme.fonts.h2,
    color: theme.colors.white,
    [theme.media.homeScreen]: {
      fontSize: theme.fonts.h1.fontSize,
    },
  },
  columnContainer: {
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  progress: {
    marginBottom: theme.marginBase * 2,
  },
  column: {
    padding: [theme.marginBase * 5, 0, theme.marginBase * 7, 0],
  },
}));

export const Home = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { data: me } = useMe();
  const { data: participations } = useParticipations();
  const completion = useProfileCompletion();

  const participationByStatus = useParticipationsByColumns();

  const acceptedParticipation: Participation | undefined =
    participationByStatus?.accepted &&
    participationByStatus.accepted.length !== 0
      ? participationByStatus.accepted[0]
      : undefined;

  const { data: sessionFromAcceptedParticipation } = usePublicSession(
    acceptedParticipation?.sessionId,
  );
  const shouldDisplayAcceptedCard =
    sessionFromAcceptedParticipation &&
    sessionFromAcceptedParticipation.status === SessionStatus.LIVE &&
    acceptedParticipation;

  const nextParticipation: Participation | undefined =
    participationByStatus?.confirmed &&
    participationByStatus.confirmed.length !== 0
      ? participationByStatus.confirmed[0]
      : undefined;

  if (!participations) {
    return <CenteredLoader />;
  }
  if (!me) {
    return <NotFound />;
  }

  return (
    <div
      className={classnames(classes.page, {
        [classes.page2Column]: !nextParticipation && !acceptedParticipation,
      })}
    >
      <div className={classes.header}>
        <TandemzLogoHeader />
        <h1 className={classes.title}>
          {me.firstName
            ? t('home.welcomeBack', { firstName: me.firstName })
            : t('home.welcomeBack_noName')}
        </h1>
      </div>
      <div className={classes.container}>
        {(nextParticipation || shouldDisplayAcceptedCard) && (
          <div className={classes.columnContainer}>
            {nextParticipation && (
              <BookedParticipationCard
                participation={nextParticipation}
                className={classes.column}
              />
            )}
            {shouldDisplayAcceptedCard && (
              <AcceptedParticipationCard
                participation={acceptedParticipation}
                className={classes.column}
              />
            )}
          </div>
        )}
        <div className={classes.columnContainer}>
          {completion?.GLOBAL?.percentage !== 100 && (
            <CardHomeBase
              style={StyleCardHome.PURPLE}
              icon="ri-user-3-line"
              title={t('home.profileCompletion.title')}
              className={classes.column}
            >
              {completion && (
                <ProgressLine
                  current={completion.GLOBAL.answered}
                  max={completion.GLOBAL.length}
                  oneline
                  className={classes.progress}
                  displayType="percent"
                  color="purple"
                  variant="white"
                  strong
                />
              )}
              <p>
                <b>{t('home.profileCompletion.descriptionBold')} </b>{' '}
                {t('home.profileCompletion.descriptionNotBold')}
              </p>
            </CardHomeBase>
          )}

          <VerifiedProfileIconsCard />

          <ApplyToStudiesCard
            participationContacted={participationByStatus?.invited}
          />
        </div>
      </div>
    </div>
  );
};
